/**
 * WigList — multi-wig service picker. Customer adds one or more wigs,
 * picks services per wig, and chooses appointment-vs-dropoff for each.
 *
 * Owns the section header + "Add wig" button + map over wigs. Each
 * row is a <WigBlock> sub-component; the same row is reused inside
 * the unified /book page after the consolidation.
 *
 * Lifted state: parent owns the WigLine[] array via a Dispatch
 * setter. We use functional updates internally so concurrent edits
 * (rapid toggling on a slow render) don't clobber each other.
 *
 * Extracted from /book/page.tsx — Phase 1.6 of booking consolidation
 * (wiki/plans/booking-consolidation-2026-04-28.md). Behavior preserved
 * verbatim: same auto-downgrade-on-invalid-location logic, same
 * canAppointment/canDropoff predicates that hide the wait-in-salon
 * button when no selected service can drive an appointment, same
 * SERVICES catalog filtered to non-deposit-redirect services.
 */
"use client";

import { useRef, useState as useReactState, type Dispatch, type SetStateAction } from "react";
import {
  Plus,
  Trash2,
  Check,
  Droplet,
  Scissors,
  Palette,
  Sparkles,
  Wrench,
  Ruler,
  Clock,
  Truck,
  type LucideIcon,
} from "lucide-react";
import { useTranslations } from "next-intl";
import {
  SERVICES,
  allowed,
  emptyWig,
  type LocationChoice,
  type WigLine,
} from "@/lib/booking-services";
import UploadSection, { type UploadedFile } from "@/components/booking/UploadSection";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { formatPrice } from "@/lib/utils";

// Per-service-type icon mapping. Lucide icons keep the boutique feel
// without becoming busy. Manny #149 — service rows now feel like a
// menu, not a form.
const SERVICE_ICONS: Record<string, LucideIcon> = {
  wash_set: Droplet,
  restyle: Scissors,
  color: Palette,
  deep_clean: Sparkles,
  repair: Wrench,
  resize: Ruler,
  new_wig: Sparkles,
};

// Translation key suffix per service id — the actual string lives in
// each locale's `booking.wlDuration_<id>` key so non-English customers
// see translated durations. Kept as a thin map so the lookup site
// reads `t(SERVICE_DURATION_KEY[svc.id])` symmetrically.
const SERVICE_DURATION_KEY: Record<string, string> = {
  wash_set: "wlDuration_wash_set",
  restyle: "wlDuration_restyle",
  color: "wlDuration_color",
  deep_clean: "wlDuration_deep_clean",
  repair: "wlDuration_repair",
  resize: "wlDuration_resize",
};

interface WigListProps {
  value: WigLine[];
  setValue: Dispatch<SetStateAction<WigLine[]>>;
}

export default function WigList({ value, setValue }: WigListProps) {
  const t = useTranslations("booking");
  const toggleService = (wigKey: string, svcId: string) => {
    let added = false;
    setValue((prev) =>
      prev.map((w) => {
        if (w.key !== wigKey) return w;
        const on = w.selectedServices.includes(svcId);
        if (!on) added = true;
        const next = on
          ? w.selectedServices.filter((s) => s !== svcId)
          : [...w.selectedServices, svcId];
        // Sanity: if the new selection no longer permits the current
        // location, downgrade to null so customer re-chooses. Two cases:
        //   1. The service being added forbids the current location
        //      (e.g. adding new_wig forbids dropoff).
        //   2. Removing the last appointment-driving service while
        //      "wait in salon" was selected — leftover services like
        //      repair/resize alone can only ride along, not drive.
        const nextSelected = SERVICES.filter((s) => next.includes(s.id));
        let location: LocationChoice | null = w.location;
        if (location === "appointment") {
          const stillCan =
            nextSelected.length > 0 &&
            nextSelected.some((s) => allowed(s, "appointment")) &&
            nextSelected.every(
              (s) =>
                allowed(s, "appointment") ||
                s.allowAppointmentWhenCombined === true
            );
          if (!stillCan) location = null;
        }
        if (location === "dropoff") {
          const stillCan =
            nextSelected.length > 0 &&
            nextSelected.every((s) => allowed(s, "dropoff"));
          if (!stillCan) location = null;
        }
        return { ...w, selectedServices: next, location };
      })
    );
    if (added) {
      // Bring the wig's "how should we handle it" section into view once the
      // selected tile re-sorts and the sub-tier/location/photos blocks mount.
      // window.scrollTo with computed coordinates is more reliable on iOS
      // Safari than scrollIntoView during this layout shift; the 650ms retry
      // catches the post-reflow position.
      const scrollToHandle = () => {
        const node = blockRefs.current[wigKey];
        if (!node) return;
        const target =
          node.querySelector<HTMLElement>("[data-location-section]") ?? node;
        const y = target.getBoundingClientRect().top + window.scrollY - 96;
        window.scrollTo({ top: y, behavior: "smooth" });
      };
      requestAnimationFrame(() => requestAnimationFrame(scrollToHandle));
      setTimeout(scrollToHandle, 650);
    }
  };

  const setWigLocation = (wigKey: string, loc: LocationChoice) => {
    setValue((prev) =>
      prev.map((w) => (w.key === wigKey ? { ...w, location: loc } : w))
    );
  };

  const setWigName = (wigKey: string, nameText: string) => {
    setValue((prev) =>
      prev.map((w) => (w.key === wigKey ? { ...w, wigName: nameText } : w))
    );
  };

  const setWigPhotos = (wigKey: string, photos: string[]) => {
    setValue((prev) =>
      prev.map((w) => (w.key === wigKey ? { ...w, photos } : w))
    );
  };

  const setSubTier = (wigKey: string, svcId: string, tierName: string) => {
    setValue((prev) =>
      prev.map((w) => {
        if (w.key !== wigKey) return w;
        const next = { ...(w.subTierByService || {}) };
        if (tierName) next[svcId] = tierName;
        else delete next[svcId];
        return { ...w, subTierByService: next };
      })
    );
  };

  // Track each wig block's DOM node so 'Add wig' can scroll the new
  // block into view + focus its nickname input — without this the new
  // block appended below the user's scroll position and they had to
  // hunt for it (#159 family of UX issues).
  const blockRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const registerBlockRef = (key: string) => (el: HTMLDivElement | null) => {
    blockRefs.current[key] = el;
  };

  const addWig = () => {
    const fresh = emptyWig(null);
    setValue((prev) => [...prev, fresh]);
    // Wait for React to mount the new block, then scroll + focus.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const node = blockRefs.current[fresh.key];
        if (!node) return;
        node.scrollIntoView({ behavior: "smooth", block: "center" });
        const nickInput = node.querySelector<HTMLInputElement>("input[type=text]");
        nickInput?.focus({ preventScroll: true });
      });
    });
  };

  const removeWig = (wigKey: string) =>
    setValue((prev) => (prev.length > 1 ? prev.filter((w) => w.key !== wigKey) : prev));

  return (
    <section className="mb-6 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-brand-dark">
            {t("wlSectionTitle")}
          </h2>
          <p className="mt-0.5 text-xs text-brand-muted">
            {t("wlSectionHint")}
          </p>
        </div>
        <button
          onClick={addWig}
          className="inline-flex items-center gap-1 rounded-md bg-brand-dark px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-gold transition-colors"
        >
          <Plus className="h-3.5 w-3.5" />
          {t("wlAddWig")}
        </button>
      </div>

      <div className="space-y-4">
        {value.map((w, idx) => (
          <WigBlock
            key={w.key}
            index={idx}
            wig={w}
            blockRef={registerBlockRef(w.key)}
            onSetName={(n) => setWigName(w.key, n)}
            onToggleService={(svcId) => toggleService(w.key, svcId)}
            onSetLocation={(loc) => setWigLocation(w.key, loc)}
            onSetPhotos={(photos) => setWigPhotos(w.key, photos)}
            onSetSubTier={(svcId, name) => setSubTier(w.key, svcId, name)}
            onRemove={() => removeWig(w.key)}
            canRemove={value.length > 1}
          />
        ))}
      </div>
    </section>
  );
}

function WigBlock({
  index,
  wig,
  blockRef,
  onSetName,
  onToggleService,
  onSetLocation,
  onSetPhotos,
  onSetSubTier,
  onRemove,
  canRemove,
}: {
  index: number;
  wig: WigLine;
  blockRef?: (el: HTMLDivElement | null) => void;
  onSetName: (v: string) => void;
  onToggleService: (svcId: string) => void;
  onSetSubTier: (svcId: string, tierName: string) => void;
  onSetLocation: (loc: LocationChoice) => void;
  onSetPhotos: (photos: string[]) => void;
  onRemove: () => void;
  canRemove: boolean;
}) {
  const t = useTranslations("booking");
  const tSvc = useTranslations("service");
  // Pricing from admin-editable retailApptServices catalog (Manny
  // #147 — bot mentions actual prices; #149 — show range inline so
  // customer knows the cost before they reach checkout).
  const {
    retailApptServices,
    retailColorServices,
    retailRepairServices,
    retailCutServices,
    retailWashSetServices,
    retailDeepCleanServices,
    retailResizeServices,
  } = useSiteConfig();
  // Sub-tier catalogs by parent service id. Every master service can
  // have its own sub-catalog now (was just color/cuts/repair). Empty
  // arrays mean no expanded picker — parent card alone.
  const subTierCatalog: Record<string, Array<{ name: string; price: number; priceMax?: number; description?: string }>> = {
    color: retailColorServices || [],
    restyle: retailCutServices || [],
    repair: retailRepairServices || [],
    wash_set: retailWashSetServices || [],
    deep_clean: retailDeepCleanServices || [],
    resize: retailResizeServices || [],
  };
  const priceLookup = (() => {
    const m = new Map<string, { min: number; max?: number; note?: string }>();
    // Seed from retailApptServices (parent rows) so admin-set parent
    // descriptions are preserved.
    for (const r of retailApptServices || []) {
      if (!r.name) continue;
      m.set(r.name.toLowerCase(), { min: r.price, max: r.priceMax, note: r.description });
    }

    const aggregateRange = (
      rows: Array<{ price: number; priceMax?: number }>
    ): { min: number; max: number } | null => {
      const valid = rows.filter((r) => r.price > 0 || (r.priceMax || 0) > 0);
      if (valid.length === 0) return null;
      let min = Infinity;
      let max = 0;
      for (const r of valid) {
        const lo = r.price > 0 ? r.price : r.priceMax || 0;
        const hi = r.priceMax && r.priceMax > 0 ? r.priceMax : r.price;
        if (lo < min) min = lo;
        if (hi > max) max = hi;
      }
      return min === Infinity ? null : { min, max };
    };

    // Sub-tier ranges OVERRIDE the parent's flat price when sub-tiers
    // exist (#165). Driven by service ID, not parent label, so
    // renaming "Wash & Set" → "Wash & Style" in admin still wires up
    // the correct sub-catalog. Single source of truth = subTierCatalog
    // map keyed by SERVICES.id.
    for (const svc of SERVICES) {
      const rows = subTierCatalog[svc.id];
      if (!rows || rows.length === 0) continue;
      const range = aggregateRange(rows);
      if (!range) continue;
      const labelKey = svc.label.toLowerCase();
      const existing = m.get(labelKey);
      m.set(labelKey, {
        min: range.min,
        max: range.max > range.min ? range.max : undefined,
        note: existing?.note,
      });
    }
    return m;
  })();

  // Local upload state — UploadSection mutates this via setValue and
  // we sync persistent URL strings back to WigList SYNCHRONOUSLY on
  // every state change (inside the setter, before React commits). This
  // guarantees that if the user clicks Submit immediately after an
  // upload finishes, the lifted WigLine.photos already has the URL —
  // no useEffect lag where the submit captured stale state.
  // Initial state seeded from wig.photos at mount (React preserves
  // local state across parent re-renders via the stable key={w.key}).
  const [photos, setPhotosLocal] = useReactState<UploadedFile[]>(() =>
    wig.photos.map((url) => ({
      url,
      type: "image/jpeg",
      name: url.split("/").pop() || "photo",
      uploading: false,
    }))
  );
  const setPhotos: Dispatch<SetStateAction<UploadedFile[]>> = (updater) => {
    setPhotosLocal((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      // Push only completed URLs up to the parent. Temp/in-flight
      // entries (uploading: true) stay local until they resolve.
      const urls = next.filter((f) => f.url && !f.uploading).map((f) => f.url);
      const sameAsParent =
        urls.length === wig.photos.length &&
        urls.every((u, i) => u === wig.photos[i]);
      if (!sameAsParent) onSetPhotos(urls);
      return next;
    });
  };
  // Of the selected services, what location choices are still valid?
  // Wait-in-salon needs (a) at least one service that natively wants
  // an appointment AND (b) every other selected service is either
  // also native-appointment OR willing to ride along
  // (allowAppointmentWhenCombined). So picking only Repair/Resize
  // hides the wait button; picking Wash + Repair shows it.
  const selected = SERVICES.filter((s) => wig.selectedServices.includes(s.id));
  const canAppointment =
    selected.length > 0 &&
    selected.some((s) => allowed(s, "appointment")) &&
    selected.every(
      (s) => allowed(s, "appointment") || s.allowAppointmentWhenCombined === true
    );
  const canDropoff =
    selected.length > 0 && selected.every((s) => allowed(s, "dropoff"));

  return (
    <div ref={blockRef} className="rounded-lg border border-gray-200 bg-gray-50/40 p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <span className="text-[11px] uppercase tracking-wider font-semibold text-gray-400">
          {t("wlWigLabel", { index: index + 1 })}
        </span>
        {canRemove && (
          <button
            onClick={onRemove}
            className="inline-flex items-center gap-1 text-xs text-red-500 hover:text-red-700 transition-colors"
          >
            <Trash2 className="h-3 w-3" />
            {t("wlRemove")}
          </button>
        )}
      </div>

      <input
        type="text"
        placeholder={t("wlNamePlaceholder")}
        value={wig.wigName}
        onChange={(e) => onSetName(e.target.value)}
        className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-transparent"
      />

      <div className="mt-3">
        <div className="text-xs font-medium text-gray-700 mb-2">{t("wlWhatServices")}</div>
        {/* Service tiles — square app-tile grid (Rivky #158). Collapsed
            tile shows only icon + name + small `+` corner — no price or
            description preview. Tapping expands the tile to col-span-full
            in place, revealing price + description + sub-tier picker.
            Other tiles stay visible so customer can add more. */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {/* Render UNSELECTED tiles first, then selected ones (Rivky
              2026-06-28). A selected service expands to col-span-full in
              place, which used to shove the remaining tiles below it —
              forcing the customer to scroll past the expanded card to add
              another service. Sinking the selected (expanded) cards to the
              bottom keeps the unselected tiles reachable at the top. */}
          {(() => {
            const visible = SERVICES.filter((s) => !s.depositRedirect);
            const ordered = [
              ...visible.filter((s) => !wig.selectedServices.includes(s.id)),
              ...visible.filter((s) => wig.selectedServices.includes(s.id)),
            ];
            return ordered;
          })().map((svc) => {
            const on = wig.selectedServices.includes(svc.id);
            const Icon = SERVICE_ICONS[svc.id] || Sparkles;
            const price = priceLookup.get(svc.label.toLowerCase());
            const priceLabel = price
              ? price.note && price.min === 0
                ? price.note
                : price.max && price.max > price.min
                ? `${formatPrice(price.min)}–${formatPrice(price.max)}`
                : price.min > 0
                ? formatPrice(price.min)
                : null
              : null;
            const durationKey = SERVICE_DURATION_KEY[svc.id];
            const duration = durationKey ? t(durationKey) : null;
            // Sort sub-tier rows cheapest-first so the customer reads
            // the cheapest option as the default starting point. Tiers
            // with priceMax sort by their lower bound; tiers with no
            // price (Quote on-site) sink to the bottom.
            const subTiers = [...(subTierCatalog[svc.id] || [])].sort((a, b) => {
              const aPrice = a.price > 0 ? a.price : (a.priceMax && a.priceMax > 0 ? a.priceMax : Infinity);
              const bPrice = b.price > 0 ? b.price : (b.priceMax && b.priceMax > 0 ? b.priceMax : Infinity);
              return aPrice - bPrice;
            });
            const pickedTier = wig.subTierByService?.[svc.id] || "";

            // Collapsed tile — square, icon centered, name below, + corner.
            if (!on) {
              return (
                <button
                  key={svc.id}
                  type="button"
                  onClick={() => onToggleService(svc.id)}
                  className="relative aspect-square rounded-2xl border border-gray-200 bg-white hover:border-brand-gold/40 hover:bg-brand-cream/30 transition-all flex flex-col items-center justify-center p-3 group"
                  aria-pressed={false}
                  aria-label={tSvc(`${svc.id}_label`)}
                >
                  {/* Plus corner — subtle white pill so it reads as a
                      tap affordance ("add this service") without competing
                      with the icon visually. */}
                  <span className="absolute top-2 end-2 w-6 h-6 rounded-full bg-white shadow-sm border border-gray-100 flex items-center justify-center text-brand-gold text-base leading-none group-hover:bg-brand-gold group-hover:text-white transition-colors">
                    <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
                  </span>
                  {/* Icon disc — bigger than the list version (12 vs 9) so
                      the tile reads as an app icon. */}
                  <div className="w-14 h-14 rounded-full bg-brand-cream flex items-center justify-center text-brand-gold mb-2.5">
                    <Icon className="h-6 w-6" />
                  </div>
                  <span className="text-[10px] sm:text-[11px] font-medium uppercase tracking-wider text-brand-dark text-center leading-tight px-1">
                    {tSvc(`${svc.id}_label`)}
                  </span>
                </button>
              );
            }

            // Expanded — full-row, breaks out of the grid via col-span-full
            // so price/description/sub-tier picker has room to render. The
            // grid auto-flow places this row in the slot the tile would
            // have occupied; surrounding tiles continue on the next row.
            return (
            <div
              key={svc.id}
              className="col-span-2 sm:col-span-3 rounded-2xl border border-brand-gold bg-brand-gold/5 shadow-sm transition-all"
            >
              <button
                type="button"
                onClick={() => onToggleService(svc.id)}
                className="w-full text-left p-3"
                aria-pressed={true}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5 w-9 h-9 rounded-full flex items-center justify-center bg-brand-gold text-white">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-medium text-brand-dark text-sm">
                        {tSvc(`${svc.id}_label`)}
                      </span>
                      {priceLabel && (
                        <span className="text-xs font-medium text-brand-gold-dark">
                          {priceLabel}
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-brand-muted mt-0.5 leading-relaxed">
                      {tSvc(`${svc.id}_description`)}
                    </p>
                    <div className="mt-2 flex items-center gap-3 text-[10px] text-brand-muted/90">
                      {duration && (
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {duration}
                        </span>
                      )}
                      {price?.max && price.max > price.min && (
                        <span>{t("wlFinalQuoteCaveat")}</span>
                      )}
                    </div>
                  </div>
                  {/* Checkmark replaces the + when selected — same corner
                      slot, same circle, different glyph. */}
                  <div className="flex-shrink-0 mt-1 w-5 h-5 rounded-full border-2 border-brand-gold bg-brand-gold flex items-center justify-center" aria-hidden="true">
                    <Check className="h-3 w-3 text-white" />
                  </div>
                </div>
              </button>
              {/* Sub-tier picker — appears only when this service is
                  selected AND has a populated catalog. Required: the
                  customer must pick a tier (or explicitly "Not sure")
                  before the booking flow lets them continue. The
                  __NOT_SURE sentinel is a real value (not just a
                  cleared field) so we can tell "haven't picked yet"
                  apart from "picked Not sure". Stored as readable
                  "Not sure" in the admin-visible notes. */}
              {on && subTiers.length > 0 && (
                <div className="border-t border-brand-gold/30 bg-white/40 p-3 space-y-1.5">
                  <div className="flex items-baseline justify-between mb-1">
                    <div className="text-[11px] font-medium uppercase tracking-wider text-brand-gold-dark">
                      {t("wlPickTier")}
                    </div>
                    {!pickedTier && (
                      <span className="text-[10px] font-medium text-amber-700">
                        {t("wlRequired")}
                      </span>
                    )}
                  </div>
                  {subTiers.map((tier) => {
                    const isPicked = pickedTier === tier.name;
                    const tierPriceLabel =
                      tier.priceMax && tier.priceMax > tier.price
                        ? `${formatPrice(tier.price)}–${formatPrice(tier.priceMax)}`
                        : tier.price > 0
                        ? formatPrice(tier.price)
                        : t("wlQuoteOnSite");
                    return (
                      <button
                        key={tier.name}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSetSubTier(svc.id, isPicked ? "" : tier.name);
                        }}
                        className={`w-full text-left rounded-md px-3 py-2 transition-colors ${
                          isPicked
                            ? "bg-brand-gold/15 ring-1 ring-brand-gold/50"
                            : "bg-white hover:bg-brand-cream/40"
                        }`}
                      >
                        <div className="flex items-baseline justify-between gap-2">
                          <span className={`text-sm font-medium ${isPicked ? "text-brand-dark" : "text-brand-dark/90"}`}>
                            {tier.name}
                          </span>
                          <span className={`text-xs font-medium flex-shrink-0 ${isPicked ? "text-brand-gold-dark" : "text-brand-muted"}`}>
                            {tierPriceLabel}
                          </span>
                        </div>
                        {tier.description && (
                          <p className="mt-0.5 text-[11px] text-brand-muted leading-relaxed">
                            {tier.description}
                          </p>
                        )}
                      </button>
                    );
                  })}
                  {(() => {
                    const NOT_SURE = "__not_sure";
                    const isNotSurePicked = pickedTier === NOT_SURE;
                    return (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSetSubTier(svc.id, isNotSurePicked ? "" : NOT_SURE);
                        }}
                        className={`w-full text-left rounded-md px-3 py-2 transition-colors ${
                          isNotSurePicked
                            ? "bg-brand-gold/15 ring-1 ring-brand-gold/50"
                            : "bg-white hover:bg-brand-cream/40"
                        }`}
                      >
                        <div className="flex items-baseline justify-between gap-2">
                          <span className={`text-sm font-medium ${isNotSurePicked ? "text-brand-dark" : "text-brand-dark/90"}`}>
                            {t("wlNotSure")}
                          </span>
                          <span className={`text-xs font-medium flex-shrink-0 ${isNotSurePicked ? "text-brand-gold-dark" : "text-brand-muted"}`}>
                            {t("wlQuoteOnSite")}
                          </span>
                        </div>
                        <p className="mt-0.5 text-[11px] text-brand-muted leading-relaxed">
                          {t("wlNotSureHint")}
                        </p>
                      </button>
                    );
                  })()}
                </div>
              )}
            </div>
            );
          })}
        </div>
      </div>

      {selected.length > 0 && (
        <div data-location-section className="mt-4 border-t border-gray-200 pt-3">
          <div className="text-xs font-medium text-gray-700 mb-2">
            {t("wlHowHandle")}
            {!canAppointment && (
              <span className="ml-1 text-[10px] text-amber-700">
                {t("wlNeedsDropoff")}
              </span>
            )}
          </div>
          {/* Hide the wait-in-salon option entirely when no selected
              service can drive an appointment. Previously we just
              disabled it; that left the customer staring at a greyed
              button thinking the form was broken. Bug #134 follow-up. */}
          <div
            className={
              canAppointment ? "grid grid-cols-2 gap-2" : "grid grid-cols-1 gap-2"
            }
          >
            {canAppointment && (
              <button
                onClick={() => onSetLocation("appointment")}
                className={`rounded-md border px-3 py-2 text-sm text-left transition-colors ${
                  wig.location === "appointment"
                    ? "border-brand-gold bg-brand-gold/10 text-brand-dark"
                    : "border-gray-200 text-brand-muted hover:border-brand-gold/40"
                }`}
              >
                {wig.location === "appointment" && (
                  <Check className="inline h-3 w-3 mr-1" />
                )}
                {t("wlWaitInSalon")}
                <span className="block text-[10px] text-gray-400">
                  {t("wlWaitHint")}
                </span>
              </button>
            )}
            <button
              disabled={!canDropoff}
              onClick={() => onSetLocation("dropoff")}
              className={`rounded-md border px-3 py-2 text-sm text-left transition-colors ${
                wig.location === "dropoff"
                  ? "border-brand-gold bg-brand-gold/10 text-brand-dark"
                  : canDropoff
                    ? "border-gray-200 text-brand-muted hover:border-brand-gold/40"
                    : "border-gray-100 text-gray-300 cursor-not-allowed"
              }`}
            >
              {wig.location === "dropoff" && (
                <Check className="inline h-3 w-3 mr-1" />
              )}
              {t("wlDropItOff")}
              <span className="block text-[10px] text-gray-400">
                {t("wlDropoffHint")}
              </span>
            </button>
          </div>
          {/* Advertise the Uber option BEFORE the customer picks a
              location — it used to appear only after selecting
              "Drop it off", so nobody knew it existed (Shmuel
              2026-07-03). */}
          {canDropoff && (
            <div className="mt-2.5 flex items-start gap-2 rounded-lg border border-brand-gold/40 bg-brand-cream/50 px-3 py-2 text-[11px] leading-relaxed text-brand-dark">
              <Truck className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-brand-gold" />
              <span>{t("wlUberAvailable")}</span>
            </div>
          )}
        </div>
      )}

      {/* Per-wig reference photos. Only renders once a service is
          picked so the form doesn't look cluttered up front. Photos
          attach to THIS wig only — admin sees them next to the wig
          name on the appointment / pickup detail panel. */}
      {selected.length > 0 && (
        <div className="mt-4 border-t border-gray-200 pt-3">
          <UploadSection
            value={photos}
            setValue={setPhotos}
            i18nNamespace="consultation"
            trackEventName="booking_file_uploaded"
          />
        </div>
      )}
    </div>
  );
}
